import type { SVGProps } from "react";

type ThriveLeafProps = SVGProps<SVGSVGElement> & {
  variant?: "mono" | "layered";
};

export function ThriveLeaf({ variant = "layered", ...props }: ThriveLeafProps) {
  if (variant === "layered") {
    /*
     * Four-facet leaf matched to the 2026-05-12 design system handoff:
     *   top-right crescent  -> teal-leaf-mid  (#87cfd4)
     *   right arc           -> teal-leaf-core (teal-600, canonical brand)
     *   bottom-left mass    -> teal-leaf-mass (teal-400, mid-brand)
     *   top-left soft       -> teal-leaf-pale (#b7dde1)
     * All four live as named tokens so a brand refresh changes the leaf
     * without touching the SVG.
     *
     * Per-path className anchors `f1` / `f2` / `f3` / `f4` are kept so
     * consumers can stage a per-facet reveal animation (the SignedOutPage
     * uses `.signedout-leaf path.f1..f4` to drive `signedout-facet-in`
     * with cascading delays). The classes are harmless when unused.
     */
    return (
      <svg
        viewBox="0 0 280 320"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        {...props}
      >
        <path
          className="f1"
          d="M249.807 53.835C268.614 77.6955 279.83 107.762 279.83 140.44C279.83 209.363 201.805 297.562 154.759 316.37C154.759 316.367 154.76 316.364 154.76 316.36C169.539 205.883 213.817 119.164 249.807 53.835Z"
          fill="var(--color-teal-leaf-mid)"
        />
        <path
          className="f2"
          d="M138.72 320C143.43 320 148.85 318.72 154.76 316.36C169.54 205.88 213.82 119.16 249.81 53.83C246.4 49.5 242.74 45.38 238.85 41.48C190.77 104.77 143.25 205.61 105.17 307.22C117.77 315.32 129.44 319.99 138.73 319.99L138.72 320Z"
          fill="var(--color-teal-leaf-core)"
        />
        <path
          className="f3"
          d="M8.79016 185.02C26.9102 234.54 69.7102 284.45 105.16 307.23C143.24 205.62 190.76 104.78 238.84 41.4899C224.51 27.1299 207.1 15.8399 187.66 8.68994C127.19 56.8299 67.9102 115.27 8.79016 185.02Z"
          fill="var(--color-teal-leaf-mass)"
        />
        <path
          className="f4"
          d="M138.72 0C155.925 0 172.413 3.07018 187.66 8.68457V8.68945C127.19 56.8294 67.91 115.27 8.79004 185.02H8.78809C3.2377 169.858 6.44659e-05 154.736 0 140.44C0 62.8805 60.7899 0.000147746 138.72 0Z"
          fill="var(--color-teal-leaf-pale)"
        />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 280 320"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M143 319.91C187.28 313.76 280 215.7 280 140.55C280 127.41 278.19 114.7 274.8 102.64L209.35 167.79C209.78 168.87 210.01 170.05 210.01 171.28C210.01 176.53 205.74 180.78 200.47 180.78C199.23 180.78 198.05 180.54 196.96 180.12L148.24 228.61C148.3 229.04 148.33 229.48 148.33 229.93C148.33 233.67 146.15 236.91 142.99 238.46V319.92L143 319.91ZM135 319.97C90.01 314.47 0 215.97 0 140.55C0 128.01 1.59 115.85 4.58 104.28L98.35 197.61C98.05 198.53 97.89 199.52 97.89 200.54C97.89 205.79 102.16 210.04 107.43 210.04C108.46 210.04 109.45 209.88 110.37 209.58L129.35 228.48C129.28 228.95 129.24 229.43 129.24 229.92C129.24 233.82 131.61 237.18 134.99 238.64V319.98L135 319.97ZM7.14 95.55L103.8 191.76C104.92 191.3 106.14 191.05 107.43 191.05C112.7 191.05 116.97 195.3 116.97 200.55C116.97 201.83 116.72 203.05 116.26 204.16L133.93 221.75C134.27 221.55 134.63 221.37 135 221.21V128.35C131.48 126.95 129 123.53 129 119.53C129 118.65 129.12 117.79 129.35 116.98L97.86 85.63C97 85.89 96.08 86.02 95.13 86.02C89.86 86.02 85.59 81.77 85.59 76.52C85.59 75.58 85.73 74.67 85.99 73.81L46.82 34.83C28.72 50.97 14.92 71.84 7.14 95.55ZM52.97 29.66L91.3 67.81C92.47 67.3 93.77 67.02 95.13 67.02C100.4 67.02 104.67 71.27 104.67 76.52C104.67 77.88 104.39 79.16 103.87 80.33L134.56 110.88C134.7 110.81 134.85 110.75 134.99 110.7V0.0700195C103.79 0.89002 75.49 11.81 52.96 29.66H52.97ZM138.43 0.0200195H139.18C139.05 0.0200195 138.93 0.0200195 138.8 0.0200195C138.67 0.0200195 138.55 0.0200195 138.42 0.0200195H138.43ZM143 0.0800195V111.12C143.14 111.19 143.27 111.27 143.4 111.34L225.5 29.62C202.6 11.87 174.06 0.98002 143 0.0800195ZM231.71 34.73L147.97 118.07C148.04 118.54 148.08 119.03 148.08 119.52C148.08 123.16 146.02 126.33 142.99 127.92V221.39C143.25 221.51 143.49 221.65 143.73 221.8L191.42 174.34C191.09 173.38 190.91 172.35 190.91 171.28C190.91 166.03 195.18 161.78 200.45 161.78C201.52 161.78 202.56 161.96 203.52 162.28L272.07 94.06C263.92 70.92 249.86 50.55 231.7 34.73H231.71Z"
      />
    </svg>
  );
}
